import React, { useEffect, useState } from "react";
import AddIcon from "@material-ui/icons/Add";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import { styles } from "./ManageSubscriptionUsersView.styles";
import * as SubscriptionAction from "../../../stores/subscriptions/SubscriptionAction";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import SubscriptionModel from "../../../models/subscription/SubscriptionModel";
import { useParams } from "react-router-dom";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { SubscriptionUserDateRangesDialog } from "./SubscriptionUserDateRangesDialog";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import PermissionGuard from "../../components/PermissionGuard/PermissionGuard";
import { isPermissionGranted } from "../../../utilities/permissionUtils";
import { useGetRoles } from "../../../hooks/useGetRole";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { SubscriptionDateRangeCopyDialog } from "./SubscriptionDateRangeCopyDialog";

export interface IManageUserSubscriptionsViewProps extends WithStyles<typeof styles> {}

const ManageUserSubscriptionsViewPure: React.FunctionComponent<IManageUserSubscriptionsViewProps> = (
    props: IManageUserSubscriptionsViewProps
) => {
    const classes = props.classes;
    const { userId } = useParams();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [showDateRangesDialog, setShowDateRangesDialog] = useState<boolean>(false);
    const [showCopyDateRangeDialog, setShowCopyDateRangeDialog] = useState<boolean>(false);
    const [showCopyAllDateRangesDialog, setShowCopyAllDateRangesDialog] = useState<boolean>(false);
    const [selectedDateRangeIndex, setSelectedDateRangeIndex] = useState<number>(null);
    const [selectedSubscriptionTypeId, setSelectedSubscriptionTypeId] = useState<number>(null);
    const [selectedSubscriptionName, setSelectedSubscriptionName] = useState<string>(null);
    const [selectedSubscriptionComment, setSelectedSubscriptionComment] = useState<string>(null);
    const [isSelectedSubscriptionFilterable, setIsSelectedSubscriptionFilterable] = useState<boolean>(false);
    const roles = useGetRoles();
    const { enqueueSnackbar } = useSnackbar();

    const requestSubscriptionsData = uId => {
        dispatch(SubscriptionAction.requestAllUserDateRanges(uId));
        if (isPermissionGranted(roles, "subscriptions.users.write"))
            dispatch(SubscriptionAction.requestUserUnassignedSubscriptionTypes(uId));
    };

    useEffect(() => {
        requestSubscriptionsData(userId);
        dispatch(UserDetailsAction.requestUser(userId));
        // eslint-disable-next-line
    }, [userId, dispatch]);

    const subscriptionsFromState = useSelector(state => state.subscriptions);
    const isRequestingSubscriptions = useSelector(state =>
        selectRequesting(state, [SubscriptionAction.REQUEST_USER_UNASSIGNED_SUBSCRIPTION_TYPES])
    );
    const isRequestingDateRanges = useSelector(state =>
        selectRequesting(state, [SubscriptionAction.REQUEST_ALL_USER_DATE_RANGES_FINISHED])
    );

    const userDetails = useSelector(state => state.userDetails?.requestedUser);

    let userDateRanges: IUniversalTableData = [];
    if (subscriptionsFromState?.allUserDateRanges) {
        Object.values(subscriptionsFromState?.allUserDateRanges)?.forEach((userDateRange: any) => {
            if (userDateRange.dateRanges?.ranges.length === 0) {
                userDateRanges.push({
                    subscriptionTypeId: userDateRange.subscriptionTypeId,
                    name: userDateRange.subscriptionName,
                    description: userDateRange.subscriptionDescription,
                    comment: userDateRange.subscriptionComment,
                    startDate: undefined,
                    endDate: undefined,
                    trial: userDateRange.trial,
                    salesTrial: userDateRange.salesTrial,
                    isFilterable: userDateRange.subscriptionIsFilterable,
                    ...(userDateRange.subscriptionIsFilterable && {
                        actionsDisabled: ["copyDateRange"]
                    })
                });
            } else {
                userDateRange.dateRanges?.ranges?.forEach(dateRange => {
                    userDateRanges.push({
                        id: dateRange.id,
                        subscriptionTypeId: userDateRange.subscriptionTypeId,
                        name: userDateRange.subscriptionName,
                        description: userDateRange.subscriptionDescription,
                        comment: userDateRange.subscriptionComment,
                        startDate: dateRange.startDate,
                        endDate: dateRange.endDate,
                        trial: dateRange.trial,
                        salesTrial: dateRange.salesTrial,
                        isFilterable: userDateRange.subscriptionIsFilterable,
                        ...((userDateRange.subscriptionIsFilterable || dateRange.salesTrial) && {
                            actionsDisabled: ["copyDateRange"]
                        })
                    });
                });
            }
        });
    }

    const userUnassignedSubscriptionTypesFromState = useSelector(
        state => state.subscriptions?.userUnassignedSubscriptionTypes
    );
    let addableSubscriptionTypes: IUniversalTableData;
    if (userUnassignedSubscriptionTypesFromState) {
        addableSubscriptionTypes = Object.values(userUnassignedSubscriptionTypesFromState)?.map(
            (subscription: SubscriptionModel) => ({
                subscriptionTypeId: subscription.id,
                name: subscription.name,
                description: subscription.description,
                isFilterable: subscription.isFilterable,
                ...((userDetails?.userLocked || subscription.isFilterable) && {
                    actionsDisabled: ["addSubscription"]
                })
            })
        );
    }

    const handleDateRangesEdit = (index: number) => {
        const selectedDateRange = userDateRanges[index];
        const subscriptionTypeId = selectedDateRange?.subscriptionTypeId as number;
        const subscriptionName = `${selectedDateRange?.name} - ${selectedDateRange?.description}`;
        setSelectedSubscriptionTypeId(subscriptionTypeId);
        setSelectedSubscriptionName(subscriptionName);
        setSelectedSubscriptionComment(selectedDateRange?.comment);
        setIsSelectedSubscriptionFilterable(selectedDateRange?.isFilterable);
        setShowDateRangesDialog(true);
    };

    const handleDelete = (index: number) => {
        const selectedDateRange = userDateRanges[index];
        const subscriptionTypeId = selectedDateRange?.subscriptionTypeId as number;
        // "empty" subscription
        if (selectedDateRange?.startDate === undefined && selectedDateRange?.endDate === undefined) {
            dispatch(SubscriptionAction.deleteSubscriptionUser(subscriptionTypeId, userId)).then(() => {
                requestSubscriptionsData(userId);
            });
        }
        // date range
        else {
            const noDateRangesPerSubscriptionType = userDateRanges.filter(
                range => range?.subscriptionTypeId === subscriptionTypeId
            ).length;
            dispatch(SubscriptionAction.deleteUserDateRange(subscriptionTypeId, userId, selectedDateRange.id)).then(
                () => {
                    // if it's the only range of this type, also delete the subscription type
                    if (noDateRangesPerSubscriptionType === 1) {
                        dispatch(SubscriptionAction.deleteSubscriptionUser(subscriptionTypeId, userId)).then(() => {
                            requestSubscriptionsData(userId);
                        });
                    } else requestSubscriptionsData(userId);
                }
            );
        }
        enqueueSnackbar("Subscription deleted", {
            variant: ToastStatusEnum.Info
        });
    };

    const handleAddSubscription = (index: number) => {
        const selectedSubscription = addableSubscriptionTypes[index];
        const subscriptionTypeId = selectedSubscription?.subscriptionTypeId as number;
        dispatch(SubscriptionAction.addSubscriptionUser(subscriptionTypeId, userId)).then(() => {
            requestSubscriptionsData(userId);
            enqueueSnackbar(t("admin.manageSubscriptionUserView.subscriptionUserAdded"), {
                variant: ToastStatusEnum.Info
            });
        });
    };

    const handleCloseDateRangesDialog = (isUpdated: boolean) => {
        setShowDateRangesDialog(false);
        if (isUpdated) {
            requestSubscriptionsData(userId);
        }
    };

    const getDisabledTooltip = index => {
        if (userDetails?.userLocked) return "Can't assign a subscription to a locked user";
        if (addableSubscriptionTypes[index]?.isFilterable)
            return "Filterable subscription can be only aded via Subscription request";
    };

    const handleCopyDateRange = (selectedUsers: any[]) => {
        setShowCopyDateRangeDialog(false);
        if (selectedUsers?.length === 0) return;

        const users = {
            users: selectedUsers?.map(u => u.id)
        };
        const subscriptionType = userDateRanges?.[selectedDateRangeIndex]?.subscriptionTypeId;
        const dateRangeId = userDateRanges?.[selectedDateRangeIndex]?.id;
        dispatch(SubscriptionAction.copyUserDateRange(subscriptionType, userDetails?.id, dateRangeId, users)).then(
            () => {
                enqueueSnackbar("The date range has been copied.", {
                    variant: ToastStatusEnum.Info
                });
            }
        );
    };

    const handleCopyAllDateRanges = (selectedUsers: any[]) => {
        setShowCopyAllDateRangesDialog(false);
        if (selectedUsers?.length === 0) return;

        const users = {
            users: selectedUsers?.map(u => u.id)
        };
        dispatch(SubscriptionAction.copyAllUserDateRanges(userDetails?.id, users)).then(() => {
            enqueueSnackbar("All user's date ranges have been copied.", {
                variant: ToastStatusEnum.Info
            });
        });
    };

    const rangesTablecolumns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: t("general.name"),
            field: "name"
        },
        {
            type: "text",
            title: t("general.description"),
            field: "description"
        },
        {
            type: "date_with_option",
            title: t("admin.manageSubscriptionUserDateRangesView.startDate"),
            field: "startDate",
            label: t("admin.manageSubscriptionUserView.unrestricted")
        },
        {
            type: "date_with_option",
            title: t("admin.manageSubscriptionUserDateRangesView.endDate"),
            field: "endDate",
            label: t("admin.manageSubscriptionUserView.unrestricted")
        },
        {
            type: "boolean",
            title: "Trial",
            field: "trial"
        },
        {
            type: "boolean",
            title: "Sales trial",
            field: "salesTrial"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "copyDateRange",
                    icon: <FileCopyIcon />,
                    tooltip: "Copy subscription to another user",
                    onClick: index => {
                        setSelectedDateRangeIndex(index);
                        setShowCopyDateRangeDialog(true);
                    },
                    scope: "subscriptions.users.write",
                    disabledTooltip: (index) => userDateRanges[index]?.subscriptionIsFilterable ? "This subscription can only be added by a subscription request." : "Sales trial cannot be copied"
                },
                {
                    name: "editDateRanges",
                    icon: <EditIcon />,
                    // tooltip: t("admin.manageSubscriptionUserDateRangesView.editDateRange"),
                    tooltip: t("general.edit"),
                    onClick: index => handleDateRangesEdit(index),
                    scope: "subscriptions.users.write"
                },
                {
                    name: "delete",
                    tooltip: t("general.delete"),
                    onDeleteFinished: index => handleDelete(index),
                    scope: "subscriptions.users.write"
                }
            ]
        }
    ];

    const subsriptionsTableColumns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: t("general.name"),
            field: "name"
        },
        {
            type: "text",
            title: t("general.description"),
            field: "description"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "addSubscription",
                    icon: <AddIcon />,
                    tooltip: t("admin.manageSubscriptionsView.addSubscription"),
                    onClick: index => handleAddSubscription(index),
                    disabledTooltip: index => getDisabledTooltip(index)
                }
            ]
        }
    ];
    const userFullName = userDetails ? `${userDetails.firstName} ${userDetails.lastName}` : "";
    const userFullDetails = userDetails ? `${userFullName} (${userDetails.email})` : "";

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    { title: t("admin.manageUsersView.manageUsers"), href: "/admin/manage-users" },
                    {
                        title: `${userFullDetails} - ${t("general.subscriptions")}`
                    }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h3">Manage user's subscriptions</Typography>
            </div>
           
            <div className={classes.subscriptionSection}>
                <div className={classes.title}>
                    {userDetails && <Typography variant="h4">{`${t("general.user")}: ${userFullDetails}`}</Typography>}
                    <Typography variant="h5">{t("admin.manageUserSubscriptionsView.listOfRanges")}</Typography>
                </div>
                <PermissionGuard scope="subscriptions.users.write">
                    <div>
                        <BlueButton onClick={() => setShowCopyAllDateRangesDialog(true)}>
                            Copy all subscriptions
                        </BlueButton>
                    </div>
                </PermissionGuard>
                <UniversalTable
                    columns={rangesTablecolumns}
                    data={userDateRanges}
                    isLoading={isRequestingSubscriptions}
                    searchable
                />
            </div>
            <PermissionGuard scope={["subscriptions.users.write"]}>
                <>
                    <div className={classes.title}>
                        <Typography variant="h5">
                            {t("admin.manageUserSubscriptionsView.addSubscriptionTitle")}
                        </Typography>
                    </div>
                    <UniversalTable
                        columns={subsriptionsTableColumns}
                        data={addableSubscriptionTypes}
                        isLoading={isRequestingDateRanges}
                        searchable
                    />
                </>
            </PermissionGuard>
            {showDateRangesDialog && (
                <SubscriptionUserDateRangesDialog
                    subscriptionId={selectedSubscriptionTypeId}
                    subscriptionTitle={selectedSubscriptionName}
                    subscriptionComment={selectedSubscriptionComment}
                    userId={userId}
                    userName={userFullName}
                    onClose={isUpdated => handleCloseDateRangesDialog(isUpdated)}
                    isFilterable={isSelectedSubscriptionFilterable}
                />
            )}
            {showCopyDateRangeDialog && (
                <SubscriptionDateRangeCopyDialog
                    title="Copy subscription date range"
                    text="Please select users to whom you want to copy selected subscription date range."
                    onCopy={handleCopyDateRange}
                    onClose={() => setShowCopyDateRangeDialog(false)}
                    subscriptionName={userDateRanges?.[selectedDateRangeIndex]?.name}
                    startDate={userDateRanges?.[selectedDateRangeIndex]?.startDate || "Unrestricted"}
                    endDate={userDateRanges?.[selectedDateRangeIndex]?.endDate || "Unrestricted"}
                />
            )}
            {showCopyAllDateRangesDialog && (
                <SubscriptionDateRangeCopyDialog
                    title="Copy all user's subscription date ranges"
                    text="Please select users to whom you want to copy all user's subscription date ranges."
                    onCopy={handleCopyAllDateRanges}
                    onClose={() => setShowCopyAllDateRangesDialog(false)}
                    userName={userFullDetails}
                />
            )}
        </CenteredContainer>
    );
};

export const ManageUserSubscriptionsView = withStyles(styles)(ManageUserSubscriptionsViewPure);
